import { useState, useRef, useEffect } from 'react';
import {
  X, Download, Calendar, Clock, MapPin, User, Phone, Mail,
  Shield, Wrench, Camera, CheckSquare, MessageSquare,
  Receipt, Building, MapPinCheck, MapPinOff, Timer, AlertCircle,
  DollarSign, TrendingUp, Package, Car, FileEdit, HelpCircle, Percent, FileText,
  Eye, EyeOff, Briefcase, Loader2, Printer
} from 'lucide-react';
import { cn } from '../../../utils/cn';
import { useUserPreferences } from '../../../contexts/UserPreferencesContext';
import { paymentBatchService } from '../../../services/paymentBatchService';
import {
  calculateInstanceFinancials,
  calculateClientBillableHours,
  calculateContractorBillableHours,
  formatCurrency,
  formatHoursMinutes
} from '../../../utils/earningsCalculations';
import { downloadPdf, printContent } from '../../../utils/pdfUtils';

// Days of the week for schedule display
const DAYS = [
  { key: 'mon', label: 'M', fullLabel: 'Monday' },
  { key: 'tue', label: 'T', fullLabel: 'Tuesday' },
  { key: 'wed', label: 'W', fullLabel: 'Wednesday' },
  { key: 'thu', label: 'T', fullLabel: 'Thursday' },
  { key: 'fri', label: 'F', fullLabel: 'Friday' },
  { key: 'sat', label: 'S', fullLabel: 'Saturday' },
  { key: 'sun', label: 'S', fullLabel: 'Sunday' }
];

// Format time from 24h to 12h or 24h based on preference
const formatTime = (time, format = '12h') => {
  if (!time) return null;
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours, 10);

  if (format === '24h') {
    return `${hours}:${minutes}`;
  }

  const suffix = h >= 12 ? 'PM' : 'AM';
  const displayHour = h % 12 || 12;
  return `${displayHour}:${minutes} ${suffix}`;
};

// Format date based on preference
const formatDate = (dateStr, format = 'DD/MM/YYYY') => {
  if (!dateStr) return 'Not set';
  const date = new Date(dateStr);

  const day = date.getDate();
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const weekday = weekdays[date.getDay()];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  switch (format) {
    case 'MM/DD/YYYY':
      return `${weekday}, ${month} ${day}, ${year}`;
    case 'YYYY-MM-DD':
      return `${weekday}, ${year} ${month} ${day}`;
    case 'DD/MM/YYYY':
    default:
      return `${weekday}, ${day} ${month} ${year}`;
  }
};

// Get status badge color
const getStatusColor = (status) => {
  const colors = {
    created: 'bg-gray-100 text-gray-700',
    assigned: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-yellow-100 text-yellow-700',
    completed: 'bg-green-100 text-green-700',
    approved: 'bg-emerald-100 text-emerald-700',
    paid: 'bg-purple-100 text-purple-700',
    cancelled: 'bg-red-100 text-red-700',
    submitted: 'bg-orange-100 text-orange-700',
    collected: 'bg-green-100 text-green-700',
    outstanding: 'bg-yellow-100 text-yellow-700'
  };
  return colors[status] || 'bg-gray-100 text-gray-700';
};

// Get priority color
const getPriorityColor = (priority) => {
  const colors = {
    high: 'bg-red-100 text-red-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-green-100 text-green-700'
  };
  return colors[priority?.toLowerCase()] || 'bg-gray-100 text-gray-700';
};

// Week circles component for recurring schedule display
const WeekCircles = ({ schedule }) => {
  const activeDays = schedule?.days || [];
  const pattern = schedule?.pattern || 'fixed';
  const fixedTime = schedule?.time;
  const customTimes = schedule?.customTimes || {};

  const getTimeForDay = (dayKey) => {
    if (pattern === 'custom') {
      return customTimes[dayKey] || null;
    }
    return fixedTime || null;
  };

  // If no days are selected, show "Custom Schedule" message
  if (!activeDays || activeDays.length === 0) {
    return (
      <p className="text-sm text-gray-600 italic">Custom schedule (varies by instance)</p>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {DAYS.map((day) => {
        const isActive = activeDays.includes(day.key);
        const time = getTimeForDay(day.key);
        return (
          <div
            key={day.key}
            className={cn(
              'w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium',
              isActive ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-400'
            )}
            title={isActive ? `${day.fullLabel}${time ? ` at ${formatTime(time)}` : ''}` : day.fullLabel}
          >
            {day.label}
          </div>
        );
      })}
    </div>
  );
};

// Format full datetime for display
const formatFullDateTime = (dateTimeStr, dateFormat = 'DD/MM/YYYY', timeFormat = '12h') => {
  if (!dateTimeStr) return 'Not recorded';
  try {
    const dt = new Date(dateTimeStr);
    const day = dt.getDate();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[dt.getMonth()];
    const year = dt.getFullYear();
    const time = dt.toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: timeFormat === '12h'
    });

    switch (dateFormat) {
      case 'MM/DD/YYYY':
        return `${month} ${day}, ${year} at ${time}`;
      case 'YYYY-MM-DD':
        return `${year} ${month} ${day} at ${time}`;
      case 'DD/MM/YYYY':
      default:
        return `${day} ${month} ${year} at ${time}`;
    }
  } catch {
    return dateTimeStr;
  }
};

// Get charge type icon
const getChargeTypeIcon = (chargeType) => {
  switch (chargeType) {
    case 'materials': return Package;
    case 'travel': return Car;
    case 'scope_change': return FileEdit;
    case 'overtime': return Clock;
    default: return HelpCircle;
  }
};

export default function Invoice({
  job,
  instance,
  company,
  onClose,
  timesheet,
  photos,
  workNotes,
  checklistData,
  questionnaireResponses,
  partnerAgency,
  additionalCharges = [],
  companyGstRate = 10
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [showClientBilling, setShowClientBilling] = useState(true);
  const [showContractorPayment, setShowContractorPayment] = useState(true);
  const [showProfitSummary, setShowProfitSummary] = useState(true);
  const [paymentBatch, setPaymentBatch] = useState(null);
  const [loadingBatch, setLoadingBatch] = useState(false);
  const invoiceRef = useRef(null);
  const { preferences } = useUserPreferences();

  // Load payment batch data if the job is paid
  useEffect(() => {
    const loadPaymentBatch = async () => {
      const isPaid = instance?.status === 'paid' || timesheet?.status === 'paid';
      if (!isPaid || !instance?.id) {
        setPaymentBatch(null);
        return;
      }

      setLoadingBatch(true);
      try {
        const batch = await paymentBatchService.getBatchForJobInstance(instance.id);
        setPaymentBatch(batch);
      } catch (err) {
        console.error('Error loading payment batch:', err);
        setPaymentBatch(null);
      } finally {
        setLoadingBatch(false);
      }
    };

    loadPaymentBatch();
  }, [instance?.id, instance?.status, timesheet?.status]);

  if (!job) return null;

  const isRecurring = job?.jobType === 'recurring';
  const isInstanceView = !!instance;
  const instances = job?.job_instances || [];
  const schedule = job?.recurringSchedule;

  // Check if report data is available
  const hasReportData = !!(timesheet || instance?.clock_in_time || instance?.clock_out_time || workNotes || checklistData || questionnaireResponses || photos?.length > 0);

  // Use job display ID as the invoice reference (single source of truth)
  const invoiceNumber = isInstanceView
    ? `${job?.jobDisplayId || 'JOB'}-#${instance?.instance_number || 1}`
    : job?.jobDisplayId || 'JOB';

  // Company initials for logo placeholder
  const companyInitials = company?.company_name
    ?.split(' ')
    ?.map(w => w[0])
    ?.join('')
    ?.substring(0, 2)
    ?.toUpperCase() || 'CO';

  // Format company address
  const formatCompanyAddress = () => {
    const parts = [];
    if (company?.address_line1) parts.push(company.address_line1);
    if (company?.address_line2) parts.push(company.address_line2);
    const cityStatePostcode = [company?.city, company?.state, company?.postcode].filter(Boolean).join(' ');
    if (cityStatePostcode) parts.push(cityStatePostcode);
    if (company?.country && company?.country !== 'Australia') parts.push(company.country);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const companyAddress = formatCompanyAddress();
  const companyPhone = company?.phone;
  const companyEmail = company?.contact_email;

  // Separate photos by type
  const beforePhotos = photos?.filter(p => p.photo_type === 'before') || [];
  const afterPhotos = photos?.filter(p => p.photo_type === 'after') || [];
  const otherPhotos = photos?.filter(p => p.photo_type !== 'before' && p.photo_type !== 'after') || [];

  // Location verification
  const clockInDistance = instance?.clock_in_distance || timesheet?.clock_in_distance;
  const clockInRadius = job?.clockInRadius;
  const isWithinRadius = clockInRadius && clockInDistance !== undefined ? clockInDistance <= clockInRadius : null;

  // Time compliance
  const estimatedHours = parseFloat(job?.estimatedHours || job?.estimated_hours) || 0;
  const actualHours = parseFloat(timesheet?.total_hours || 0);
  const getTimeCompliance = () => {
    if (!estimatedHours || !actualHours) return null;
    const diff = actualHours - estimatedHours;
    if (diff <= 0) return { status: 'on-time', label: 'On Time', color: 'bg-green-100 text-green-700' };
    if (diff <= 0.5) return { status: 'slight-overtime', label: 'Slight Overtime', color: 'bg-yellow-100 text-yellow-700' };
    return { status: 'overtime', label: 'Overtime', color: 'bg-red-100 text-red-700' };
  };
  const timeCompliance = getTimeCompliance();

  // ===== FINANCIAL CALCULATIONS =====
  // Prepare job object with snake_case fields for calculation function
  const jobForCalc = {
    client_charge_type: job?.clientChargeType || job?.client_charge_type || 'hourly',
    client_charge_amount: job?.clientChargeAmount || job?.client_charge_amount,
    client_include_gst: job?.clientIncludeGst ?? job?.client_include_gst ?? true,
    client_gst_type: job?.clientGstType || job?.client_gst_type || 'exclusive',
    payment_type: job?.paymentType || job?.payment_type || 'hourly',
    payment_amount: job?.paymentAmount || job?.payment_amount,
    contractor_include_gst: job?.contractorIncludeGst ?? job?.contractor_include_gst ?? false,
    contractor_gst_type: job?.contractorGstType || job?.contractor_gst_type || 'exclusive',
    estimated_hours: estimatedHours,
    job_source: job?.jobSource || job?.job_source,
    partner_agency: partnerAgency || job?.partnerAgency || job?.partner_agency
  };

  // Prepare instance for calculation
  const instanceForCalc = {
    timesheet: timesheet ? [timesheet] : instance?.timesheet,
    overtime_request: instance?.overtime_request,
    charge_adjustment: instance?.charge_adjustment,
    charge_adjustment_reason: instance?.charge_adjustment_reason,
    additional_charges: additionalCharges.length > 0 ? additionalCharges : (instance?.additional_charges || [])
  };

  // Calculate financials
  const financials = calculateInstanceFinancials(instanceForCalc, jobForCalc, companyGstRate);

  // Additional charges from instance or props
  const allAdditionalCharges = additionalCharges.length > 0 ? additionalCharges : (instance?.additional_charges || []);
  const totalAdditionalCharges = allAdditionalCharges.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
  const totalAdditionalGst = allAdditionalCharges.reduce((sum, c) => sum + (c.include_gst ? (parseFloat(c.gst_amount) || 0) : 0), 0);

  // Client payment status
  const clientPaymentReceived = instance?.client_payment_received;
  const clientPaymentReceivedAt = instance?.client_payment_received_at;

  // Contractor payment status - use instance status as primary (reflects actual payment state)
  const contractorPaymentStatus = instance?.status || timesheet?.status;
  const isPaid = contractorPaymentStatus === 'paid';

  // Calculate totals
  const baseChargeNet = (financials.chargeToClient || 0) - (financials.gstOnCharge || 0);
  const overtimeChargeNet = (financials.overtimeChargeToClient || 0) - (financials.gstOnOvertimeCharge || 0);
  const totalClientCharge = (financials.chargeToClient || 0) +
    (financials.hasOvertime && financials.overtimeApproved ? (financials.overtimeChargeToClient || 0) : 0) +
    totalAdditionalCharges + totalAdditionalGst;

  const totalContractorPay = (financials.payoutToContractor || 0) +
    (financials.hasOvertime && financials.overtimeApproved ? (financials.overtimePayoutToContractor || 0) : 0);

  const totalProfit = (financials.profit || 0) +
    (financials.hasOvertime && financials.overtimeApproved ? (financials.overtimeProfit || 0) : 0) +
    totalAdditionalCharges; // Additional charges go straight to profit

  const profitMargin = totalClientCharge > 0 ? ((totalProfit / (totalClientCharge - (financials.gstOnCharge || 0) - (financials.gstOnOvertimeCharge || 0) - totalAdditionalGst)) * 100) : 0;

  // Handle PDF download using server-side Puppeteer rendering
  const handleDownloadPDF = async () => {
    if (!invoiceRef.current) return;

    setIsDownloading(true);
    await downloadPdf({
      contentRef: invoiceRef,
      filename: `Invoice-${invoiceNumber}`,
      onSuccess: () => {
        // PDF downloaded successfully
      },
      onError: (message) => {
        console.error('Failed to generate PDF:', message);
        alert('Failed to generate PDF. Please try again.');
      },
      onDevFallback: () => {
        alert('PDF download requires deployment. Use Print → Save as PDF for now.');
      }
    });
    setIsDownloading(false);
  };

  // Handle print using iframe for clean output
  const handlePrint = async () => {
    if (!invoiceRef.current) return;

    setIsPrinting(true);
    await printContent({
      contentRef: invoiceRef,
      onComplete: () => {
        setIsPrinting(false);
      }
    });
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 pt-20">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-xl print:hidden">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-orange-500" />
            Tax Invoice
          </h2>

          {/* Financial Section Toggles */}
          {isInstanceView && hasReportData && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 mr-1">Show:</span>
              <button
                onClick={() => setShowClientBilling(!showClientBilling)}
                className={cn(
                  'px-2 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1',
                  showClientBilling
                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                )}
                title={showClientBilling ? 'Hide Client Billing' : 'Show Client Billing'}
              >
                {showClientBilling ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                Client
              </button>
              <button
                onClick={() => setShowContractorPayment(!showContractorPayment)}
                className={cn(
                  'px-2 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1',
                  showContractorPayment
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                )}
                title={showContractorPayment ? 'Hide Contractor Payment' : 'Show Contractor Payment'}
              >
                {showContractorPayment ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                Contractor
              </button>
              <button
                onClick={() => setShowProfitSummary(!showProfitSummary)}
                className={cn(
                  'px-2 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1',
                  showProfitSummary
                    ? 'bg-gray-700 text-white hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                )}
                title={showProfitSummary ? 'Hide Profit Summary' : 'Show Profit Summary'}
              >
                {showProfitSummary ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                Profit
              </button>
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={handleDownloadPDF}
              disabled={isDownloading}
              className={cn(
                "flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg transition-colors",
                isDownloading ? "opacity-50 cursor-not-allowed" : "hover:bg-orange-600"
              )}
            >
              <Download className="w-4 h-4" />
              {isDownloading ? 'Generating...' : 'Download PDF'}
            </button>
            <button
              onClick={handlePrint}
              disabled={isPrinting}
              className={cn(
                "flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg transition-colors",
                isPrinting ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-200"
              )}
            >
              <Printer className="w-4 h-4" />
              {isPrinting ? 'Preparing...' : 'Print'}
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Invoice Content */}
        <div className="flex-1 overflow-auto p-6">
          <div ref={invoiceRef} className="bg-white" style={{ minWidth: '700px' }}>
            {/* Invoice Header */}
            <div className="flex justify-between items-start mb-8 pb-6 border-b-2 border-orange-500">
              <div className="flex items-center gap-4">
                {company?.logo_url ? (
                  <img
                    src={company.logo_url}
                    alt={company?.company_name || 'Company'}
                    className="w-16 h-16 rounded-lg object-cover shadow-md"
                    crossOrigin="anonymous"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white text-xl font-bold shadow-md">
                    {companyInitials}
                  </div>
                )}
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{company?.company_name || 'Company Name'}</h1>
                  {companyAddress && <p className="text-sm text-gray-500 mt-1">{companyAddress}</p>}
                  <div className="flex items-center gap-4 mt-1">
                    {companyPhone && (
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {companyPhone}
                      </p>
                    )}
                    {companyEmail && (
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {companyEmail}
                      </p>
                    )}
                  </div>
                  {company?.abn && (
                    <p className="text-xs text-gray-500 mt-1">ABN: {company.abn}</p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-orange-500 mb-2">TAX INVOICE</p>
                <p className="text-lg font-mono text-gray-700">{invoiceNumber}</p>
                <p className="text-sm text-gray-500 mt-2">
                  Generated: {new Date().toLocaleDateString('en-AU', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>

            {/* Client Details (new) or Partner Agency (legacy) */}
            {(job?.client || partnerAgency || job?.partnerAgency) && (
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Client</h3>
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <div className="flex items-start gap-3">
                    <Building className="w-5 h-5 text-purple-600 mt-0.5" />
                    <div className="flex-1">
                      {/* New client structure */}
                      {job?.client ? (
                        <>
                          <p className="font-semibold text-gray-900">
                            {job.client.name || 'Client'}
                          </p>
                          {job.client.contact_person && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                              <User className="w-3 h-3" />
                              {job.client.contact_person}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-4 mt-2">
                            {job.client.contact_email && (
                              <p className="text-sm text-gray-600 flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {job.client.contact_email}
                              </p>
                            )}
                            {job.client.contact_phone && (
                              <p className="text-sm text-gray-600 flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {job.client.contact_phone}
                              </p>
                            )}
                          </div>
                        </>
                      ) : (
                        /* Legacy: partnerAgency fallback */
                        <>
                          <p className="font-semibold text-gray-900">
                            {partnerAgency?.agency_name || job?.partnerAgency?.agency_name || 'Partner Agency'}
                          </p>
                          {(partnerAgency?.contact_person || job?.partnerAgency?.contact_person) && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                              <User className="w-3 h-3" />
                              {partnerAgency?.contact_person || job?.partnerAgency?.contact_person}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-4 mt-2">
                            {(partnerAgency?.contact_email || job?.partnerAgency?.contact_email) && (
                              <p className="text-sm text-gray-600 flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {partnerAgency?.contact_email || job?.partnerAgency?.contact_email}
                              </p>
                            )}
                            {(partnerAgency?.contact_phone || job?.partnerAgency?.contact_phone) && (
                              <p className="text-sm text-gray-600 flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {partnerAgency?.contact_phone || job?.partnerAgency?.contact_phone}
                              </p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    {/* Commission Info */}
                    {financials.commissionMargin !== null && financials.commissionMargin > 0 && (
                      <div className="text-right">
                        <p className="text-xs text-gray-400 uppercase">Commission</p>
                        <p className="text-sm font-medium text-purple-700">
                          {job?.client?.commission_rate || partnerAgency?.commission_rate || job?.partnerAgency?.commission_rate}%
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Job Site & Job Summary */}
            <div className="grid grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Job Site</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  {(job?.jobSite || job?.serviceReceiver) ? (
                    <>
                      {/* New structure: jobSite */}
                      {job?.jobSite ? (
                        <>
                          <p className="font-semibold text-gray-900">{job.jobSite.name || job.jobSite.address || 'N/A'}</p>
                          {job.jobSite.address && job.jobSite.name && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                              <MapPin className="w-3 h-3" /> {job.jobSite.address}
                            </p>
                          )}
                          {job.jobSite.contact_person && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                              <User className="w-3 h-3" /> {job.jobSite.contact_person}
                            </p>
                          )}
                          {job.jobSite.contact_email && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                              <Mail className="w-3 h-3" /> {job.jobSite.contact_email}
                            </p>
                          )}
                          {job.jobSite.contact_phone && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                              <Phone className="w-3 h-3" /> {job.jobSite.contact_phone}
                            </p>
                          )}
                        </>
                      ) : (
                        /* Legacy fallback: serviceReceiver */
                        <>
                          <p className="font-semibold text-gray-900">{job.serviceReceiver.receiver_name || 'N/A'}</p>
                          {job.serviceReceiver.contact_person && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                              <User className="w-3 h-3" /> {job.serviceReceiver.contact_person}
                            </p>
                          )}
                          {job.serviceReceiver.contact_email && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                              <Mail className="w-3 h-3" /> {job.serviceReceiver.contact_email}
                            </p>
                          )}
                          {job.serviceReceiver.contact_phone && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                              <Phone className="w-3 h-3" /> {job.serviceReceiver.contact_phone}
                            </p>
                          )}
                          {job.serviceReceiver.address && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                              <MapPin className="w-3 h-3" /> {job.serviceReceiver.address}
                            </p>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-500">No Job Site Assigned</p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Job Summary</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className={cn('px-2 py-1 rounded text-xs font-medium', isRecurring ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700')}>
                      {isRecurring ? 'Recurring' : 'One-Time'}
                    </span>
                    <span className={cn('px-2 py-1 rounded text-xs font-medium', getPriorityColor(job?.priority))}>
                      {job?.priority || 'Normal'} Priority
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Created:</span> {formatDate(job?.createdAt, preferences.date_format)}
                  </p>
                </div>
              </div>
            </div>

            {/* Job Details */}
            <div className="mb-8">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Job Details</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{job?.jobName || 'Untitled Job'}</h4>
                {job?.description && <p className="text-sm text-gray-600 mb-4">{job.description}</p>}

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-orange-500 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-gray-400 uppercase">Location</p>
                      <p className="text-sm text-gray-900">{job?.jobSite?.address || job?.locationAddress || 'Not specified'}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Calendar className="w-4 h-4 text-orange-500 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-gray-400 uppercase">Schedule</p>
                      {isInstanceView ? (
                        // For instance view, show the specific instance date/time
                        <p className="text-sm text-gray-900">
                          {formatDate(instance?.scheduled_date, preferences.date_format)}
                          {instance?.scheduled_time && ` at ${formatTime(instance.scheduled_time, preferences.time_format)}`}
                        </p>
                      ) : isRecurring ? (
                        // For recurring jobs without instance, show the schedule pattern
                        <div className="mt-1">
                          <WeekCircles schedule={schedule} />
                        </div>
                      ) : (
                        // For one-time jobs, show the job scheduled date
                        <p className="text-sm text-gray-900">
                          {formatDate(job?.scheduledDate, preferences.date_format)}
                          {job?.scheduledTime && ` at ${formatTime(job.scheduledTime, preferences.time_format)}`}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <User className="w-4 h-4 text-orange-500 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-gray-400 uppercase">Contractor</p>
                      <p className="text-sm text-gray-900">
                        {isInstanceView
                          ? (instance?.contractor?.full_name || instance?.contractor?.fullName || 'Unassigned')
                          : (job?.assignedContractor?.fullName || 'Unassigned')}
                      </p>
                    </div>
                  </div>

                  {estimatedHours > 0 && (
                    <div className="flex items-start gap-3">
                      <Clock className="w-4 h-4 text-orange-500 mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-gray-400 uppercase">Estimated Duration</p>
                        <p className="text-sm text-gray-900">{formatHoursMinutes(estimatedHours)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Safety & Equipment */}
            {(job?.safetyRequirements || job?.equipmentList) && (
              <div className="grid grid-cols-2 gap-6 mb-8">
                {job?.safetyRequirements && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Shield className="w-4 h-4" /> Safety Requirements
                    </h3>
                    <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                      <p className="text-sm text-gray-700">{job.safetyRequirements}</p>
                    </div>
                  </div>
                )}
                {job?.equipmentList && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Wrench className="w-4 h-4" /> Equipment Needed
                    </h3>
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <p className="text-sm text-gray-700">{job.equipmentList}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Other Details */}
            {job?.otherDetails && (
              <div className="mb-8">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Additional Information
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{job.otherDetails}</p>
                </div>
              </div>
            )}

            {/* Instance Details */}
            {isInstanceView && instance && (
              <div className="mb-8">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Instance Details</h3>
                <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-500 uppercase">ID:</span>
                      <span className="text-sm font-bold text-orange-600 whitespace-nowrap">{job?.jobDisplayId || 'JOB'}-#{instance.instance_number}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs text-gray-500">Scheduled:</span>
                      <span className="text-sm text-gray-900">{formatDate(instance.scheduled_date, preferences.date_format)}</span>
                    </div>
                    {instance.scheduled_time && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-xs text-gray-500">Start Time:</span>
                        <span className="text-sm text-gray-900">{formatTime(instance.scheduled_time, preferences.time_format)}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-500">Status:</span>
                      <span className={cn('px-2 py-0.5 rounded text-xs font-medium', getStatusColor(instance.status))}>
                        {(instance.status || 'created').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Compliance Indicators */}
            {isInstanceView && hasReportData && (isWithinRadius !== null || timeCompliance) && (
              <div className="mb-8">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Compliance Indicators</h3>
                <div className="flex flex-wrap gap-4">
                  {isWithinRadius !== null && (
                    <div className={cn(
                      'flex items-center gap-2 px-4 py-2 rounded-lg',
                      isWithinRadius ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                    )}>
                      {isWithinRadius ? <MapPinCheck className="w-5 h-5 text-green-600" /> : <MapPinOff className="w-5 h-5 text-red-600" />}
                      <div>
                        <p className={cn('text-sm font-medium', isWithinRadius ? 'text-green-700' : 'text-red-700')}>
                          {isWithinRadius ? 'Within Location Radius' : 'Outside Location Radius'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {clockInDistance !== undefined ? `${Math.round(clockInDistance)}m from site` : ''}
                          {clockInRadius ? ` (${clockInRadius}m required)` : ''}
                        </p>
                      </div>
                    </div>
                  )}
                  {timeCompliance && (
                    <div className={cn(
                      'flex items-center gap-2 px-4 py-2 rounded-lg border',
                      timeCompliance.status === 'on-time' ? 'bg-green-50 border-green-200' :
                      timeCompliance.status === 'slight-overtime' ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'
                    )}>
                      <Timer className={cn(
                        'w-5 h-5',
                        timeCompliance.status === 'on-time' ? 'text-green-600' :
                        timeCompliance.status === 'slight-overtime' ? 'text-yellow-600' : 'text-red-600'
                      )} />
                      <div>
                        <p className={cn(
                          'text-sm font-medium',
                          timeCompliance.status === 'on-time' ? 'text-green-700' :
                          timeCompliance.status === 'slight-overtime' ? 'text-yellow-700' : 'text-red-700'
                        )}>
                          {timeCompliance.label}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatHoursMinutes(actualHours)} actual vs {formatHoursMinutes(estimatedHours)} estimated
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Submitted Report Details */}
            {isInstanceView && hasReportData && (
              <div className="mb-8">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Submitted Report Details</h3>
                <div className="space-y-4">
                  {/* Timesheet */}
                  {(timesheet || instance?.clock_in_time) && (
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                      <h4 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4" /> Timesheet Details
                      </h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs font-medium text-gray-400 uppercase">Clock In</p>
                          <p className="text-sm text-gray-900 font-medium">
                            {formatFullDateTime(timesheet?.clock_in_time || instance?.clock_in_time, preferences.date_format, preferences.time_format)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-400 uppercase">Clock Out</p>
                          <p className="text-sm text-gray-900 font-medium">
                            {formatFullDateTime(timesheet?.clock_out_time || instance?.clock_out_time, preferences.date_format, preferences.time_format)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-400 uppercase">Total Duration</p>
                          <p className="text-sm text-gray-900 font-medium">
                            {timesheet?.total_hours ? formatHoursMinutes(parseFloat(timesheet.total_hours)) : 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Before Photos */}
                  <div className="photo-section bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                      <Camera className="w-4 h-4" /> Before Photos {beforePhotos.length > 0 && `(${beforePhotos.length})`}
                    </h4>
                    {beforePhotos.length > 0 ? (
                      <div className="grid grid-cols-4 gap-3">
                        {beforePhotos.map((photo, idx) => (
                          <div key={photo.id || idx} className="relative aspect-square bg-gray-200 rounded-lg overflow-hidden">
                            {(photo.url || photo.storage_path) ? (
                              <img src={photo.url || photo.storage_path} alt={`Before ${idx + 1}`} className="w-full h-full object-cover" crossOrigin="anonymous" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400"><Camera className="w-8 h-8" /></div>
                            )}
                            <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-xs font-medium bg-blue-500 text-white">Before</span>
                          </div>
                        ))}
                      </div>
                    ) : job?.photosRequiredBefore ? (
                      <p className="text-sm text-orange-600 italic">Not submitted by contractor</p>
                    ) : (
                      <p className="text-sm text-gray-500 italic">Not required</p>
                    )}
                  </div>

                  {/* After Photos */}
                  <div className="photo-section bg-green-50 rounded-lg p-4 border border-green-200">
                    <h4 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
                      <Camera className="w-4 h-4" /> After Photos {afterPhotos.length > 0 && `(${afterPhotos.length})`}
                    </h4>
                    {afterPhotos.length > 0 ? (
                      <div className="grid grid-cols-4 gap-3">
                        {afterPhotos.map((photo, idx) => (
                          <div key={photo.id || idx} className="relative aspect-square bg-gray-200 rounded-lg overflow-hidden">
                            {(photo.url || photo.storage_path) ? (
                              <img src={photo.url || photo.storage_path} alt={`After ${idx + 1}`} className="w-full h-full object-cover" crossOrigin="anonymous" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400"><Camera className="w-8 h-8" /></div>
                            )}
                            <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-xs font-medium bg-green-500 text-white">After</span>
                          </div>
                        ))}
                      </div>
                    ) : job?.photosRequiredAfter ? (
                      <p className="text-sm text-orange-600 italic">Not submitted by contractor</p>
                    ) : (
                      <p className="text-sm text-gray-500 italic">Not required</p>
                    )}
                  </div>

                  {/* Work Notes */}
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <h4 className="text-sm font-semibold text-purple-800 mb-3 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" /> Work Notes
                    </h4>
                    {(workNotes || instance?.work_notes) ? (
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{workNotes || instance?.work_notes}</p>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No notes provided</p>
                    )}
                  </div>

                  {/* Checklist */}
                  <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                    <h4 className="text-sm font-semibold text-teal-800 mb-3 flex items-center gap-2">
                      <CheckSquare className="w-4 h-4" /> Checklist
                    </h4>
                    {(() => {
                      const data = checklistData || instance?.checklist_data;
                      const items = data ? (Array.isArray(data) ? data : (data?.items || Object.entries(data || {}).map(([key, value]) => ({ label: key, checked: value })))) : [];
                      const hasData = items && items.length > 0;

                      if (hasData) {
                        return (
                          <div className="space-y-2">
                            {items.map((item, idx) => (
                              <div key={idx} className="flex items-center gap-2">
                                <span className={cn(
                                  'w-5 h-5 rounded flex items-center justify-center text-xs',
                                  (item.checked || item.completed) ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-400'
                                )}>
                                  {(item.checked || item.completed) ? '✓' : ''}
                                </span>
                                <span className={cn('text-sm', (item.checked || item.completed) ? 'text-gray-900' : 'text-gray-500')}>
                                  {item.label || item.text || item.name || `Item ${idx + 1}`}
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      } else if (job?.checklistTemplate && job.checklistTemplate.length > 0) {
                        return <p className="text-sm text-orange-600 italic">Not submitted by contractor</p>;
                      } else {
                        return <p className="text-sm text-gray-500 italic">Not applicable</p>;
                      }
                    })()}
                  </div>

                  {/* Questionnaire */}
                  <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                    <h4 className="text-sm font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" /> Questionnaire Responses
                    </h4>
                    {(() => {
                      const data = questionnaireResponses || instance?.questionnaire_responses;
                      const items = data ? (Array.isArray(data) ? data : Object.entries(data || {}).map(([question, answer]) => ({ question, answer }))) : [];
                      const hasData = items && items.length > 0;

                      if (hasData) {
                        return (
                          <div className="space-y-3">
                            {items.map((item, idx) => (
                              <div key={idx} className="border-b border-indigo-200 pb-2 last:border-0 last:pb-0">
                                <p className="text-xs font-medium text-indigo-600 uppercase">{item.question || item.label || `Question ${idx + 1}`}</p>
                                <p className="text-sm text-gray-900 mt-1">{item.answer || item.response || item.value || 'No response'}</p>
                              </div>
                            ))}
                          </div>
                        );
                      } else if (job?.questionnaireTemplate && job.questionnaireTemplate.length > 0) {
                        return <p className="text-sm text-orange-600 italic">Not submitted by contractor</p>;
                      } else {
                        return <p className="text-sm text-gray-500 italic">Not applicable</p>;
                      }
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* ===== FINANCIAL SECTION ===== */}
            {isInstanceView && hasReportData && (
              <div className="mb-8">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Financial Details
                </h3>

                <div className="space-y-4">
                  {/* Client Billing Section */}
                  {showClientBilling && (
                  <div className="financial-section bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="text-sm font-semibold text-blue-800 mb-3">Charge to Service Receiver (Client Billing)</h4>
                    <table className="w-full text-sm">
                      <tbody>
                        {/* Base Charge */}
                        <tr>
                          <td className="py-1 text-gray-600">Billable Hours (Client)</td>
                          <td className="py-1 text-right text-gray-900">{financials.clientBillableHours || 0} hrs</td>
                        </tr>
                        <tr>
                          <td className="py-1 text-gray-600">Rate</td>
                          <td className="py-1 text-right text-gray-900">
                            {jobForCalc.client_charge_type === 'hourly'
                              ? `${formatCurrency(jobForCalc.client_charge_amount)}/hr`
                              : `${formatCurrency(jobForCalc.client_charge_amount)} (fixed)`}
                          </td>
                        </tr>
                        <tr className="border-t border-blue-200">
                          <td className="py-1 text-gray-700 font-medium">Base Charge (excl GST)</td>
                          <td className="py-1 text-right text-gray-900 font-medium">{formatCurrency(baseChargeNet)}</td>
                        </tr>
                        {financials.chargeHasGst && (
                          <tr>
                            <td className="py-1 text-gray-600">GST ({companyGstRate}%)</td>
                            <td className="py-1 text-right text-gray-900">{formatCurrency(financials.gstOnCharge)}</td>
                          </tr>
                        )}

                        {/* Charge Adjustment */}
                        {instance?.charge_adjustment !== null && instance?.charge_adjustment !== undefined && (
                          <>
                            <tr className="border-t border-blue-200">
                              <td className="py-1 text-orange-700 font-medium" colSpan={2}>
                                Charge Adjustment Applied
                              </td>
                            </tr>
                            <tr>
                              <td className="py-1 text-gray-600 pl-4">Reason: {instance.charge_adjustment_reason || 'Not specified'}</td>
                              <td className="py-1 text-right text-orange-700 font-medium">{formatCurrency(instance.charge_adjustment)}</td>
                            </tr>
                          </>
                        )}

                        {/* Overtime (if approved) */}
                        {financials.hasOvertime && financials.overtimeApproved && (
                          <>
                            <tr className="border-t border-blue-200">
                              <td className="py-1 text-gray-700 font-medium" colSpan={2}>Overtime (Approved)</td>
                            </tr>
                            <tr>
                              <td className="py-1 text-gray-600 pl-4">Overtime Hours</td>
                              <td className="py-1 text-right text-gray-900">{financials.clientOvertimeHours} hrs</td>
                            </tr>
                            <tr>
                              <td className="py-1 text-gray-600 pl-4">Overtime Charge</td>
                              <td className="py-1 text-right text-gray-900">{formatCurrency(overtimeChargeNet)}</td>
                            </tr>
                            {financials.chargeHasGst && financials.gstOnOvertimeCharge > 0 && (
                              <tr>
                                <td className="py-1 text-gray-600 pl-4">GST on Overtime</td>
                                <td className="py-1 text-right text-gray-900">{formatCurrency(financials.gstOnOvertimeCharge)}</td>
                              </tr>
                            )}
                          </>
                        )}
                        {financials.hasOvertime && financials.overtimePending && (
                          <tr className="border-t border-blue-200">
                            <td className="py-1 text-yellow-700" colSpan={2}>Overtime Pending Approval</td>
                          </tr>
                        )}

                        {/* Additional Charges */}
                        {allAdditionalCharges.length > 0 && (
                          <>
                            <tr className="border-t border-blue-200">
                              <td className="py-1 text-gray-700 font-medium" colSpan={2}>Additional Charges</td>
                            </tr>
                            {allAdditionalCharges.map((charge, idx) => {
                              const IconComponent = getChargeTypeIcon(charge.charge_type);
                              return (
                                <tr key={idx}>
                                  <td className="py-1 text-gray-600 pl-4 flex items-center gap-2">
                                    <IconComponent className="w-3 h-3" />
                                    {charge.description || charge.charge_type}
                                    {charge.include_gst && ' (incl GST)'}
                                  </td>
                                  <td className="py-1 text-right text-gray-900">
                                    {formatCurrency(parseFloat(charge.amount) + (charge.include_gst ? parseFloat(charge.gst_amount || 0) : 0))}
                                  </td>
                                </tr>
                              );
                            })}
                          </>
                        )}

                        {/* Total Client Charge */}
                        <tr className="border-t-2 border-blue-300 bg-blue-100">
                          <td className="py-2 text-blue-900 font-bold">TOTAL DUE FROM CLIENT</td>
                          <td className="py-2 text-right text-blue-900 font-bold text-lg">{formatCurrency(totalClientCharge)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  )}

                  {/* Contractor Payment Section */}
                  {showContractorPayment && (
                  <div className="financial-section bg-green-50 rounded-lg p-4 border border-green-200">
                    <h4 className="text-sm font-semibold text-green-800 mb-3">Pay to Contractor</h4>
                    <table className="w-full text-sm">
                      <tbody>
                        <tr>
                          <td className="py-1 text-gray-600">Billable Hours (Contractor)</td>
                          <td className="py-1 text-right text-gray-900">{financials.contractorBillableHours || 0} hrs</td>
                        </tr>
                        <tr>
                          <td className="py-1 text-gray-600">Rate</td>
                          <td className="py-1 text-right text-gray-900">
                            {jobForCalc.payment_type === 'hourly'
                              ? `${formatCurrency(jobForCalc.payment_amount)}/hr`
                              : `${formatCurrency(jobForCalc.payment_amount)} (fixed)`}
                          </td>
                        </tr>
                        <tr className="border-t border-green-200">
                          <td className="py-1 text-gray-700 font-medium">Base Pay</td>
                          <td className="py-1 text-right text-gray-900 font-medium">
                            {formatCurrency(financials.payoutToContractor - (financials.gstOnPayout || 0))}
                          </td>
                        </tr>
                        {financials.payoutHasGst && financials.gstOnPayout > 0 && (
                          <tr>
                            <td className="py-1 text-gray-600">GST (Contractor registered)</td>
                            <td className="py-1 text-right text-gray-900">{formatCurrency(financials.gstOnPayout)}</td>
                          </tr>
                        )}

                        {/* Overtime Pay */}
                        {financials.hasOvertime && financials.overtimeApproved && financials.overtimePayoutToContractor > 0 && (
                          <>
                            <tr className="border-t border-green-200">
                              <td className="py-1 text-gray-700 font-medium" colSpan={2}>Overtime Pay (Approved)</td>
                            </tr>
                            <tr>
                              <td className="py-1 text-gray-600 pl-4">Overtime Amount</td>
                              <td className="py-1 text-right text-gray-900">
                                {formatCurrency(financials.overtimePayoutToContractor - (financials.gstOnOvertimePayout || 0))}
                              </td>
                            </tr>
                            {financials.payoutHasGst && financials.gstOnOvertimePayout > 0 && (
                              <tr>
                                <td className="py-1 text-gray-600 pl-4">GST on Overtime</td>
                                <td className="py-1 text-right text-gray-900">{formatCurrency(financials.gstOnOvertimePayout)}</td>
                              </tr>
                            )}
                          </>
                        )}

                        {/* Total Contractor Pay */}
                        <tr className="border-t-2 border-green-300 bg-green-100">
                          <td className="py-2 text-green-900 font-bold">TOTAL PAY TO CONTRACTOR</td>
                          <td className="py-2 text-right text-green-900 font-bold text-lg">{formatCurrency(totalContractorPay)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  )}

                  {/* Partner Agency Commission */}
                  {showProfitSummary && financials.isPartnerAgency && financials.commissionMargin > 0 && (
                    <div className="financial-section bg-purple-50 rounded-lg p-4 border border-purple-200">
                      <h4 className="text-sm font-semibold text-purple-800 mb-3 flex items-center gap-2">
                        <Percent className="w-4 h-4" /> Partner Agency Commission
                      </h4>
                      <table className="w-full text-sm">
                        <tbody>
                          <tr>
                            <td className="py-1 text-gray-600">Commission Rate</td>
                            <td className="py-1 text-right text-gray-900">
                              {partnerAgency?.commission_rate || job?.partnerAgency?.commission_rate || 0}%
                            </td>
                          </tr>
                          <tr>
                            <td className="py-1 text-gray-600">Applied to Base Charge (excl GST)</td>
                            <td className="py-1 text-right text-gray-900">{formatCurrency(baseChargeNet)}</td>
                          </tr>
                          <tr className="border-t border-purple-200 bg-purple-100">
                            <td className="py-2 text-purple-900 font-bold">COMMISSION AMOUNT</td>
                            <td className="py-2 text-right text-purple-900 font-bold">{formatCurrency(financials.commissionMargin)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Profit Summary */}
                  {showProfitSummary && (
                  <div className="financial-section bg-gray-900 rounded-lg p-4 text-white">
                    <h4 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" /> Profit Summary
                    </h4>
                    <table className="w-full text-sm">
                      <tbody>
                        <tr>
                          <td className="py-1 text-gray-400">Gross Revenue (Client Charge excl GST)</td>
                          <td className="py-1 text-right text-gray-200">
                            {formatCurrency(baseChargeNet + overtimeChargeNet + totalAdditionalCharges)}
                          </td>
                        </tr>
                        {financials.isPartnerAgency && financials.commissionMargin > 0 && (
                          <tr>
                            <td className="py-1 text-gray-400">Less: Partner Agency Commission</td>
                            <td className="py-1 text-right text-red-400">-{formatCurrency(financials.commissionMargin)}</td>
                          </tr>
                        )}
                        <tr>
                          <td className="py-1 text-gray-400">Less: Contractor Pay (excl GST)</td>
                          <td className="py-1 text-right text-red-400">
                            -{formatCurrency(totalContractorPay - (financials.gstOnPayout || 0) - (financials.gstOnOvertimePayout || 0))}
                          </td>
                        </tr>
                        <tr className="border-t border-gray-700">
                          <td className="py-2 text-white font-bold">NET PROFIT</td>
                          <td className="py-2 text-right text-green-400 font-bold text-lg">{formatCurrency(totalProfit)}</td>
                        </tr>
                        <tr>
                          <td className="py-1 text-gray-400">Profit Margin</td>
                          <td className="py-1 text-right text-green-400 font-medium">{profitMargin.toFixed(1)}%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  )}

                  {/* Payment Status */}
                  <div className="financial-section bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">Payment Status</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-medium text-gray-400 uppercase mb-1">Client Payment</p>
                        <span className={cn(
                          'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium',
                          clientPaymentReceived ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        )}>
                          {clientPaymentReceived ? 'Collected' : 'Outstanding'}
                        </span>
                        {clientPaymentReceived && clientPaymentReceivedAt && (
                          <p className="text-xs text-gray-500 mt-1">
                            Collected: {formatDate(clientPaymentReceivedAt, preferences.date_format)}
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-400 uppercase mb-1">Contractor Payment</p>
                        <span className={cn(
                          'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium',
                          isPaid ? 'bg-purple-100 text-purple-700' :
                          contractorPaymentStatus === 'approved' ? 'bg-green-100 text-green-700' :
                          'bg-orange-100 text-orange-700'
                        )}>
                          {isPaid ? 'Paid' : contractorPaymentStatus === 'approved' ? 'Approved' : 'Pending'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Payment Confirmation Section - Only show when paid and Contractor view is on */}
                  {isPaid && showContractorPayment && (
                    <div className="financial-section bg-purple-50 rounded-lg p-4 border border-purple-200">
                      <h4 className="text-sm font-semibold text-purple-800 mb-3 flex items-center gap-2">
                        <Receipt className="w-4 h-4" />
                        Payment Confirmation
                      </h4>

                      {loadingBatch ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />
                        </div>
                      ) : paymentBatch ? (
                        <div className="space-y-3">
                          {/* Payment Type Badge */}
                          <div className="flex items-center gap-2">
                            <span className={cn(
                              'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                              paymentBatch.timesheets?.length > 1
                                ? 'bg-purple-200 text-purple-800'
                                : 'bg-gray-200 text-gray-700'
                            )}>
                              {paymentBatch.timesheets?.length > 1 ? 'Batch Payment' : 'Single Payment'}
                            </span>
                          </div>

                          {/* Transaction Details */}
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-xs text-purple-600 font-medium">Transaction Reference</p>
                              <p className="text-gray-900 font-mono">{paymentBatch.transaction_ref}</p>
                            </div>
                            <div>
                              <p className="text-xs text-purple-600 font-medium">Total Payment</p>
                              <p className="text-gray-900 font-bold">{formatCurrency(paymentBatch.total_amount)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-purple-600 font-medium">Payment Date</p>
                              <p className="text-gray-900">{formatDate(paymentBatch.created_at, preferences.date_format)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-purple-600 font-medium">Recorded By</p>
                              <p className="text-gray-900">{paymentBatch.created_by_user?.full_name || 'Unknown'}</p>
                            </div>
                          </div>

                          {/* Jobs in this Payment */}
                          {paymentBatch.timesheets?.length > 1 && (
                            <div className="mt-3 pt-3 border-t border-purple-200">
                              <p className="text-xs text-purple-600 font-medium mb-2">Jobs Included in this Payment</p>
                              <div className="space-y-1">
                                {paymentBatch.timesheets.map((ts) => {
                                  // Calculate earnings using the standard calculation (min 1hr rule)
                                  const instanceForCalc = {
                                    timesheet: [ts],
                                    overtime_request: ts.job_instance?.overtime_request
                                  };
                                  const tsJob = ts.job_instance?.job;
                                  const tsFinancials = calculateInstanceFinancials(instanceForCalc, tsJob);
                                  const tsCalculatedAmount = (tsFinancials.payoutToContractor || 0) +
                                    (tsFinancials.hasOvertimeApproved ? (tsFinancials.overtimePayoutToContractor || 0) : 0);

                                  return (
                                    <div key={ts.id} className="flex items-center justify-between text-sm py-1 px-2 bg-white rounded">
                                      <div className="flex items-center gap-2">
                                        <Briefcase className="w-3 h-3 text-purple-500" />
                                        <span className="text-gray-800">{ts.job_instance?.job?.job_name || 'Unknown Job'}</span>
                                        <span className="text-gray-500 text-xs">({ts.contractor?.full_name})</span>
                                      </div>
                                      <span className="text-gray-700 font-medium">{formatCurrency(tsCalculatedAmount)}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Proof of Payment */}
                          {paymentBatch.proof_url && (
                            <div className="mt-3 pt-3 border-t border-purple-200">
                              <p className="text-xs text-purple-600 font-medium mb-2">Proof of Payment</p>
                              <img
                                src={paymentBatch.proof_url}
                                alt="Proof of payment"
                                className="max-h-32 rounded border border-purple-200"
                              />
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">
                          No batch payment details available. This job may have been marked as paid before the batch payment system was implemented.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="border-t-2 border-gray-200 pt-6 mt-8 pb-8">
              <div className="flex justify-between text-xs text-gray-400">
                <div>
                  <p>Created: {formatDate(job?.createdAt, preferences.date_format)}</p>
                  {job?.updatedAt && <p>Last Updated: {formatDate(job?.updatedAt, preferences.date_format)}</p>}
                </div>
                <div className="text-right">
                  <p>Tax Invoice generated by Mervo</p>
                  <p>{new Date().toLocaleString('en-AU')}</p>
                </div>
              </div>
              {company?.payment_terms && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-600">
                    <strong>Payment Terms:</strong> {company.payment_terms}
                  </p>
                </div>
              )}
              {company?.bank_details && (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-600">
                    <strong>Bank Details:</strong> {company.bank_details}
                  </p>
                </div>
              )}
              {/* Powered by Mervo */}
              <div className="flex items-center justify-center gap-2 py-4 mt-4">
                <span className="text-xs text-gray-400">Powered by</span>
                <div className="w-5 h-5 bg-gradient-to-br from-orange-500 to-orange-600 rounded flex items-center justify-center text-white text-xs font-bold">
                  M
                </div>
                <span className="text-xs text-gray-500 font-semibold">Mervo</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
